import { defineStore } from 'pinia';
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useApi } from '../composables/useApi';
import TokenService from '../services/tokenService';
import { supabase } from '@/lib/supabase';
import { useMasterDataStore } from './masterData';

export const useAuthStore = defineStore('auth', () => {
  const token = ref(TokenService.getAccessToken());
  const refreshToken = ref(TokenService.getRefreshToken());
  const user = ref(null);
  const loading = ref(false);
  const initialized = ref(false);
  const api = useApi();
  const masterDataStore = useMasterDataStore();
  let refreshPromise = null;
  let authStateUnsubscribe = null;

  // Storage event handler for cross-tab synchronization
  const handleStorageChange = (event) => {
    if (event.key === 'auth_token' && !event.newValue) {
      clearAuthState();
    } else if (event.key === 'token') {
      // Token changed in another tab
      if (event.newValue) {
        // New token set (login in another tab)
        token.value = event.newValue;
        updateAuthHeader(event.newValue);
        // Fetch user data if not already loaded
        if (!user.value) {
          fetchUser().catch(error => {
            console.error('Failed to fetch user after token change:', error);
          });
        }
      } else {
        // Token removed (logout in another tab)
        clearAuthState();
      }
    } else if (event.key === 'refreshToken') {
      // Refresh token changed
      refreshToken.value = event.newValue;
    } else if (event.key === 'tokenTimestamp') {
      // Token timestamp changed, which means tokens were updated in another tab
      // This helps ensure we catch all token changes
      const storedToken = TokenService.getAccessToken();
      if (storedToken && storedToken !== token.value) {
        token.value = storedToken;
        updateAuthHeader(storedToken);
        // Fetch user data if not already loaded
        if (!user.value) {
          fetchUser().catch(error => {
            console.error('Failed to fetch user after token timestamp change:', error);
          });
        }
      }
    }
  };

  // Setup and cleanup for storage event listener
  if (typeof window !== 'undefined') {
    // Add event listener when the store is created
    window.addEventListener('storage', handleStorageChange);
  }

  // Setup and cleanup for storage event listener and Supabase auth listener
  if (typeof window !== 'undefined') {
    // Set up Supabase auth state change listener
    authStateUnsubscribe = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('Supabase auth state changed:', event, session?.user?.email);
      
      // Only process SIGNED_IN events (actual logins), skip INITIAL_SESSION to prevent excessive logging
      // INITIAL_SESSION fires on every page refresh/tab open, which causes unnecessary audit logs
      if (event === 'SIGNED_IN' && session) {
        // Skip if we're already authenticated with this user
        if (isAuthenticated.value && user.value?.email === session.user.email) {
          console.log('Already authenticated with this user, skipping supabase-login call');
          return;
        }
        
        try {
          // User is authenticated with Supabase
          const response = await api.post('/auth/supabase-login', {
            supabaseToken: session.access_token
          });
          
          const { token: accessToken, refreshToken: newRefreshToken, user: userData } = response.data;
          
          TokenService.setTokens(accessToken, newRefreshToken);
          token.value = accessToken;
          refreshToken.value = newRefreshToken;
          user.value = userData;
          updateAuthHeader(accessToken);
          initialized.value = true;
        } catch (error) {
          console.error('Failed to sync Supabase session:', error);
          clearAuthState();
        }
      } else if (event === 'INITIAL_SESSION' && session) {
        // For INITIAL_SESSION, only sync if we don't have auth state but have a valid session
        // This handles page refreshes without creating audit logs
        if (!isAuthenticated.value && !user.value) {
          try {
            const response = await api.post('/auth/supabase-login', {
              supabaseToken: session.access_token
            });
            
            const { token: accessToken, refreshToken: newRefreshToken, user: userData } = response.data;
            
            TokenService.setTokens(accessToken, newRefreshToken);
            token.value = accessToken;
            refreshToken.value = newRefreshToken;
            user.value = userData;
            updateAuthHeader(accessToken);
            initialized.value = true;
          } catch (error) {
            console.error('Failed to sync Supabase session on INITIAL_SESSION:', error);
            // Don't clear auth state on INITIAL_SESSION errors, just log
          }
        }
      } else if (event === 'SIGNED_OUT') {
        clearAuthState();
      }
    });
  }

  // Computed property for authentication state
  const isAuthenticated = computed(() => !!token.value && !!user.value);

  // Computed property for checking permissions
  const hasPermission = (permission) => {
    return user.value?.permissions?.includes(permission) || false;
  };

  // Computed property for checking multiple permissions
  const hasAnyPermission = (permissions) => {
    return permissions.some(permission => hasPermission(permission));
  };

  // Set up auth header for the auth API instance
  const updateAuthHeader = (newToken) => {
    if (newToken) {
      api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    } else {
      delete api.defaults.headers.common['Authorization'];
    }
  };

  // Initialize auth state
  const initialize = async () => {
    if (initialized.value) {
      console.log('Auth already initialized');
      // CRITICAL: Check if we still have valid authentication
      if (!isAuthenticated.value) {
        // Check if we have tokens but they might be expired
        const storedToken = TokenService.getAccessToken();
        if (storedToken && TokenService.isTokenExpired(storedToken)) {
          console.log('Token expired, clearing auth state');
          clearAuthState();
          return false;
        }
      }
      return isAuthenticated.value;
    }
    
    try {
      loading.value = true;
      console.log('Initializing auth state...');
      
      // Check initial session
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError) {
        console.error('Failed to get Supabase session:', sessionError);
        clearAuthState();
        initialized.value = true;
        return false;
      }
      
      if (session) {
        console.log('Found existing Supabase session');
        
        // Reduced wait time - listener should process quickly
        // Only wait if user is not already set
        if (!user.value) {
          // Brief wait for auth state listener to process
          await new Promise(resolve => setTimeout(resolve, 100));
        }
        
        // If listener didn't set user, manually sync
        if (!user.value) {
          try {
            const response = await api.post('/auth/supabase-login', {
              supabaseToken: session.access_token
            });
            
            const { token: accessToken, refreshToken: newRefreshToken, user: userData } = response.data;
            
            TokenService.setTokens(accessToken, newRefreshToken);
            token.value = accessToken;
            refreshToken.value = newRefreshToken;
            user.value = userData;
            updateAuthHeader(accessToken);
          } catch (error) {
            console.error('Failed to sync Supabase session:', error);
            clearAuthState();
            initialized.value = true;
            return false;
          }
        }
      } else if (token.value) {
        // No Supabase session but we have a legacy token
        console.log('No Supabase session, checking legacy token');
        
        // Check if token is expired
        if (TokenService.isTokenExpired(token.value)) {
          console.log('Legacy token expired, clearing auth state');
          clearAuthState();
          initialized.value = true;
          return false;
        }
        
        try {
          await fetchUser();
        } catch (error) {
          console.error('Failed to fetch user with legacy token:', error);
          clearAuthState();
          initialized.value = true;
          return false;
        }
      }
      
      initialized.value = true;
      return isAuthenticated.value; // Return actual auth status
    } catch (error) {
      console.error('Failed to initialize auth:', error);
      clearAuthState();
      initialized.value = true;
      return false;
    } finally {
      loading.value = false;
    }
  };

  // Clear auth state without side effects
  const clearAuthState = () => {
    TokenService.clearTokens();
    token.value = null;
    refreshToken.value = null;
    user.value = null;
    updateAuthHeader(null);
    initialized.value = true;
  };

  // Refresh access token
  async function refreshAccessToken() {
    // If there's already a refresh in progress, return that promise
    if (refreshPromise) {
      return refreshPromise;
    }

    const storedRefreshToken = TokenService.getRefreshToken();
    if (!storedRefreshToken) {
      clearAuthState();
      throw new Error('No refresh token available');
    }

    refreshPromise = new Promise(async (resolve, reject) => {
      try {
        const response = await api.post('/auth/refresh', {
          refreshToken: storedRefreshToken
        });

        const { accessToken: newAccessToken, refreshToken: newRefreshToken, user: userData } = response.data;
        
        if (!newAccessToken || !newRefreshToken) {
          throw new Error('Invalid token response');
        }

        // Update tokens
        TokenService.setTokens(newAccessToken, newRefreshToken);
        token.value = newAccessToken;
        refreshToken.value = newRefreshToken;
        updateAuthHeader(newAccessToken);

        // Update user data if provided
        if (userData) {
          user.value = userData;
        }
        
        resolve(newAccessToken);
      } catch (error) {
        console.error('Token refresh failed:', error);
        clearAuthState();
        reject(error);
      } finally {
        refreshPromise = null;
      }
    });

    return refreshPromise;
  }

  // Fetch current user data
  async function fetchUser() {
    try {
      // Check if token needs refresh before making the request
      if (TokenService.shouldRefresh(token.value)) {
        await refreshAccessToken();
      }

      const response = await api.get('/auth/me');
      if (!response.data) {
        throw new Error('No user data received');
      }
      
      const userData = response.data;
      console.log('User data fetched:', {
        id: userData.id,
        email: userData.email,
        role: userData.role,
        permissions: userData.permissions
      });
      
      if (!userData.permissions || !Array.isArray(userData.permissions)) {
        console.error('Invalid permissions data:', userData.permissions);
        throw new Error('Invalid permissions data');
      }
      
      user.value = userData;
      return userData;
    } catch (error) {
      if (error.response?.status === 401) {
        try {
          await refreshAccessToken();
          // Retry the request with new token
          const response = await api.get('/auth/me');
          user.value = response.data;
          return response.data;
        } catch (refreshError) {
          console.error('Failed to refresh token:', refreshError);
          throw refreshError;
        }
      }
      console.error('Failed to fetch user:', error);
      throw error;
    }
  }

  // Login user (Supabase only - migration complete)
  const login = async (email, password) => {
    try {
      loading.value = true;
      console.log('Starting Supabase login for:', email);
      
      // Login with Supabase
      const { data: { session }, error: supabaseError } = await supabase.auth.signInWithPassword({
        email,
        password
      });
      
      if (supabaseError) {
        console.error('Supabase login error:', supabaseError);
        
        // SECURITY: Log failed login attempt to backend (for notifications and monitoring)
        try {
          await api.post('/auth/log-failed-login', { email });
          console.log('Failed login attempt logged to backend');
        } catch (logError) {
          console.error('Failed to log failed login attempt:', logError);
          // Don't block the login flow if logging fails
        }
        
        // Handle specific Supabase errors
        if (supabaseError.message === 'Email not confirmed') {
          return {
            success: false,
            error: 'Please check your email to confirm your account before logging in.'
          };
        }
        
        if (supabaseError.message === 'Invalid login credentials') {
          return {
            success: false,
            error: 'Invalid email or password. Please try again.'
          };
        }
        
        return {
          success: false,
          error: supabaseError.message || 'Login failed. Please try again.'
        };
      }
      
      if (session) {
        console.log('Supabase login successful, getting JWT');
        // Supabase login successful, get our JWT
        const response = await api.post('/auth/supabase-login', {
          supabaseToken: session.access_token
        });
        
        const { token: accessToken, refreshToken: newRefreshToken, user: userData } = response.data;
        
        TokenService.setTokens(accessToken, newRefreshToken);
        token.value = accessToken;
        refreshToken.value = newRefreshToken;
        user.value = userData;
        updateAuthHeader(accessToken);
        initialized.value = true;
        
        // CRITICAL: Wait for state to be fully set before returning
        // This ensures isAuthenticated is true before redirect happens
        await new Promise(resolve => setTimeout(resolve, 100));
        
        console.log('Login complete, isAuthenticated:', isAuthenticated.value);
        
        return {
          success: true,
          user: userData
        };
      }
      
      return {
        success: false,
        error: 'Invalid credentials'
      };
    } catch (error) {
      console.error('Login process error:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Invalid email or password'
      };
    } finally {
      loading.value = false;
    }
  };

  // Logout user
  const logout = async () => {
    try {
      // Sign out from Supabase
      await supabase.auth.signOut();
      
      // Sign out from our backend
      await api.post('/auth/logout', {
        refreshToken: TokenService.getRefreshToken()
      });
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      clearAuthState();
      masterDataStore.clearCache(); // Clear master data cache on logout
    }
  };

  // REMOVED: Migration functions - all users migrated to Supabase

  // Cleanup function to remove event listeners when store is no longer used
  const cleanup = () => {
    if (typeof window !== 'undefined') {
      window.removeEventListener('storage', handleStorageChange);
    }
    if (authStateUnsubscribe) {
      authStateUnsubscribe();
      authStateUnsubscribe = null;
    }
  };

  return {
    token,
    refreshToken,
    user,
    loading,
    initialized,
    isAuthenticated,
    hasPermission,
    hasAnyPermission,
    initialize,
    login,
    logout,
    cleanup
  };
}); 